import { useMemo } from 'react';

import { formatCurrency, isE2E } from '@cast/utils';
import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  HexIcon,
  Icons,
  Line,
  Placeholder,
  Stack,
  TimeXAxis,
  Typography,
  YAxis,
} from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  ActionRequired,
  useNetworkCostDisplayState,
  useNetworkCostOverTimeQuery,
} from '@cast/split-me/features/cluster/cost-report';
import { HiddenDot } from '@cast/split-me/components';
import { attachTimeTicks } from '@cast/split-me/utils';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from '@cast/split-me/types';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { ChartWithTopWorkloadsCard } from '../../../_components/ui/ChartWithTopWorkloadsCard';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { useNetworkCostAdjustedDateRange } from './_hooks/useNetworkCostAdjustedDateRange';

type ChartDatum = TopMostExpensiveGroupDatapoint[number] & {
  chartTimestampUnix: number;
};

type NetworkCostChartProps = {
  chartData: ChartDatum[];
  topItems: TopMostExpensiveGroup[];
  isLoading: boolean;
  timeZone: string;
};

const NetworkCostChart = ({
  chartData,
  topItems,
  isLoading,
  timeZone,
}: NetworkCostChartProps) => (
  <Stack flex={1} minHeight={200}>
    <ChartContainer<ChartDatum>
      data={chartData}
      isLoading={isLoading}
      sx={{ width: '100%', height: '100%' }}
      margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
      testId="network-cost-over-time-chart"
    >
      <ChartGrid hideVertical />
      {topItems.map((item) => (
        <Line<ChartDatum>
          key={item.id}
          dataKey={(d) => d.values[item.id] ?? 0}
          stroke={item.color}
          strokeWidth={2}
          dot={isE2E() ? HiddenDot : false}
          tooltipConfig={{ displayName: item.name }}
        />
      ))}
      <YAxis
        tickCount={5}
        tickFormatter={(value: string | number) => `$${value}`}
        padding={{ top: 30 }}
        allowDecimals
      />
      <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
      <ChartTooltip<ChartDatum>
        testId="data-popper"
        header={(headerProps) => (
          <ChartTooltipTimestampHeader {...headerProps} timezone={timeZone} />
        )}
        formatValue={(value) =>
          formatCurrency(Number(value), { maximumFractionDigits: 2 })
        }
      />
    </ChartContainer>
  </Stack>
);

export const NetworkCostOverTimeCard = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { currentDateRange, adjustedDateRange } =
    useNetworkCostAdjustedDateRange();
  const {
    topItems,
    datapoints,
    isLoading,
    isFetching,
    isCapabilitiesLoading,
    hasNetworkAgent,
    isDataProcessing,
    error,
    refetch,
  } = useNetworkCostOverTimeQuery({ currentDateRange, adjustedDateRange });
  const { getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<string>();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const chartData = useMemo<ChartDatum[]>(
    () => attachTimeTicks(datapoints || []),
    [datapoints]
  );

  const visibleTopItems = useMemo(
    () => topItems.filter((item) => !isMetricDisabled(item.id)),
    [topItems, isMetricDisabled]
  );

  const { showActionRequired, showNoData, showProcessing } =
    useNetworkCostDisplayState({
      isLoading,
      isCapabilitiesLoading,
      hasNetworkAgent,
      isDataProcessing,
      dataLength: topItems.length,
    });

  if (hasError && !isFetching) {
    return (
      <WidgetLayout testId="network-cost-over-time-card" padding={false}>
        <FailedToLoad title="Failed to load network cost" refresh={refetch} />
      </WidgetLayout>
    );
  }

  if (showActionRequired) {
    return (
      <WidgetLayout testId="network-cost-over-time-card" padding={false}>
        <ActionRequired />
      </WidgetLayout>
    );
  }

  if (showNoData) {
    return (
      <WidgetLayout testId="network-cost-over-time-card" padding={false}>
        <Placeholder
          title={showProcessing ? 'Data is being processed' : 'No data found'}
          body={
            showProcessing
              ? 'For newly connected clusters, it might take up to 15 minutes for the data to be available.'
              : 'Try selecting a different time period.'
          }
          icon={
            <HexIcon
              variant="notification"
              icon={
                showProcessing ? (
                  <Icons.HourglassHigh weight="fill" />
                ) : (
                  <Icons.Question weight="fill" />
                )
              }
              size={84}
            />
          }
        />
      </WidgetLayout>
    );
  }

  return (
    <ChartWithTopWorkloadsCard
      testId="network-cost-over-time-card"
      leftHeader={<Typography variant="A1">Network cost</Typography>}
      rightHeader={<Typography variant="A1">Highest to Lowest</Typography>}
      chart={
        <NetworkCostChart
          chartData={chartData}
          topItems={visibleTopItems}
          isLoading={isCardLoading}
          timeZone={timeZone}
        />
      }
      items={topItems.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        value: formatCurrency(item.cost, { maximumFractionDigits: 2 }),
      }))}
      isListLoading={isCardLoading && !topItems.length}
      onItemClick={toggleMetric}
      isItemDisabled={isMetricDisabled}
      getItemHref={(item) =>
        getWorkloadDashboardPath(item.id, WORKLOAD_CATEGORIES.NETWORK)
      }
    />
  );
};
